import {
  AddressType,
  ShippingTypeBookingTemplates,
  BookingTemplateConstants,
} from "@assets/constants";
import {
  BookingTemplateForm,
  defaultCargoForm,
  defaultRouteForm,
  PartnerForm,
} from "@assets/formTypes/bookingTemplates";
import { Company, BookingTemplateTypes, RegistryTypes } from "@assets/types";
import {
  ArrayUtilities,
  EnumUtilities,
  ObjectUtilities,
} from "@assets/utilities";
import { singleValueToSelectOptionMapper } from "@assets/utilities/labelMapperUtils";
import AuthService from "@services/AuthService";
import PortService from "@services/PortService";
import RegistryService from "@services/RegistryService";

/**
 * Convert a BookingTemplate to a TemplateForm
 */
export const bookingTemplateToTemplateForm = async (
  bookingTemplate: BookingTemplateTypes.BookingTemplate,
  incoterms: RegistryTypes.Incoterm[]
): Promise<BookingTemplateForm> => {
  let fromCountry = defaultRouteForm.FromCountry,
    toCountry = defaultRouteForm.ToCountry,
    countryOfOrigin = defaultCargoForm.CountryOfOrigin;

  try {
    if (bookingTemplate.PLR) {
      fromCountry = await RegistryService.getCountry(
        bookingTemplate.PLR.CountryCode
      );
    }
  } catch {
    // Ignore registry fail
  }

  try {
    if (bookingTemplate.PFD) {
      toCountry = await RegistryService.getCountry(
        bookingTemplate.PFD.CountryCode
      );
    }
  } catch {
    // Ignore registry fail
  }

  try {
    if (bookingTemplate.CountryOfOrigin) {
      countryOfOrigin = await RegistryService.getCountry(
        bookingTemplate.CountryOfOrigin
      );
    }
  } catch {
    // Ignore registry fail
  }

  const incotermPoint = bookingTemplate.IncoTermsPoint
    ? await PortService.getPort(bookingTemplate.IncoTermsPoint)
    : null;

  return {
    BookingTemplateName: bookingTemplate.TemplateName,
    BookingParty: bookingTemplate.BookingParty ?? "",
    BookingPartyType:
      EnumUtilities.enumFromValue(
        bookingTemplate.BookingPartyType,
        AddressType
      ) ?? defaultRouteForm.BookingPartyType,
    Shipper: bookingTemplate.Shipper
      ? await bookingTemplatePartnerToPartnerForm(bookingTemplate.Shipper)
      : null,
    Consignee: bookingTemplate.Consignee
      ? await bookingTemplatePartnerToPartnerForm(bookingTemplate.Consignee)
      : null,
    Notifier: bookingTemplate.Notifier
      ? await bookingTemplatePartnerToPartnerForm(bookingTemplate.Notifier)
      : null,
    FromTransferPlace: bookingTemplate.FromTransferPlaceType,
    FromCountry: fromCountry,
    PLR: bookingTemplate.PLR,
    POL: bookingTemplate.POL,
    FromStops: bookingTemplate.FromTransportRequests,
    ToTransferPlace: bookingTemplate.ToTransferPlaceType,
    ToCountry: toCountry,
    PFD: bookingTemplate.PFD,
    POD: bookingTemplate.POD,
    ToStops: bookingTemplate.ToTransportRequests,
    Incoterm:
      incoterms.find(
        (incoterm) => incoterm.TermsCode === bookingTemplate.IncoTerms
      ) ?? null,
    IncotermPoint: incotermPoint,
    ShippingType:
      EnumUtilities.enumFromValue(
        bookingTemplate.ShippingType,
        ShippingTypeBookingTemplates
      ) ?? defaultCargoForm.ShippingType,
    Details:
      bookingTemplate.Details.length > 0
        ? bookingTemplate.Details.map((detail) => {
            return {
              ...detail,
              Temperature: singleValueToSelectOptionMapper(detail.Temperature),
            };
          })
        : defaultCargoForm.Details,
    HSCodes: bookingTemplate.HSCodes,
    BillOfLadingType:
      ArrayUtilities.getObject(
        BookingTemplateConstants.BILLS,
        bookingTemplate.BOLType,
        "value"
      ) ?? null,
    CountryOfOrigin: countryOfOrigin,
    Comment: bookingTemplate.Remark,
  };
};

//--  Convert a company to PartnerForm in BookingTemplates
export const companyToPartnerForm = async (
  company: Company
): Promise<PartnerForm> => {
  return {
    PartnerFillOutType:
      BookingTemplateConstants.PARTNER_FILL_OUT_TYPE.BOOKINGPARTY,
    PartnerCompany: companyToPartner(company),
    CompanyName: company.FullName,
    StreetAndNumber: company.Address1 || "",
    City: company.Address2 || "",
    PostalCode: company.PostCode,
    County: company.Address3 || "",
    Country: company.Address4 || "",
    AdditionalAddressInfo: "",
    ContactName: "",
    Email: "",
    Phone: "",
    CityPort: company.PointCode
      ? await PortService.getPort(company.PointCode)
      : null,
  };
};

//--  Helper mapper to convert company to PartnerCompany
export const companyToPartner = (
  company: Company
): BookingTemplateTypes.Partner => {
  return {
    PartnerCode: company.PartnerCode,
    CompanyName: company.FullName,
    StreetAndNumber: company.Address1,
    City: company.Address2,
    PostalCode: company.PostCode,
    County: company.Address3,
    Country: company.Address4,
    AdditionalAddressInfo: "",
    ContactName: "",
    Email: "",
    Phone: "",
    PointCode: company.PointCode,
  };
};

// A mapper that we need to get the correct Partner object with PointCode and PartnerCode as PartnerCompany
export const bookingTemplatePartnerToPartnerForm = async (
  bookingTemplatePartner: BookingTemplateTypes.BookingTemplatePartner
): Promise<PartnerForm> => {
  let partnerCompany = defaultRouteForm.PartnerCompany;
  try {
    if (bookingTemplatePartner.PartnerCode) {
      partnerCompany = await AuthService.getCompany(
        bookingTemplatePartner.PartnerCode
      );
    }
  } catch {
    // Ignore registry fail
  }

  let partnerPointCode = defaultRouteForm.PointCode;
  try {
    if (bookingTemplatePartner.PointCode) {
      partnerPointCode =
        (await PortService.getPort(bookingTemplatePartner.PointCode)) ??
        partnerPointCode;
    }
  } catch {
    // Ignore registry fail
  }

  return {
    ...bookingTemplatePartner,
    PartnerCompany: partnerCompany,
    CityPort: partnerPointCode,
  };
};

// A mapper that we use to create the object we want to send to the backend, extracting the PartnerCompany -> partnerCode
export const partnerFormToBookingTemplatePartner = (
  partnerForm: PartnerForm
): BookingTemplateTypes.BookingTemplatePartner => {
  return {
    PartnerFillOutType: partnerForm.PartnerFillOutType,
    CompanyName: partnerForm.CompanyName,
    StreetAndNumber: partnerForm.StreetAndNumber,
    City: partnerForm.City,
    PostalCode: partnerForm.PostalCode,
    Phone: partnerForm.Phone,
    County: partnerForm.County,
    Country: partnerForm.Country,
    AdditionalAddressInfo: partnerForm.AdditionalAddressInfo,
    ContactName: partnerForm.ContactName,
    Email: partnerForm.Email,
    PartnerCode: partnerForm.PartnerCompany?.PartnerCode ?? "",
    PointCode: partnerForm.CityPort?.PointCode ?? "",
  };
};

// Convert a BookingTemplateForm to a CreateUpdateBookingTemplateVM
export const templateFormToCreateUpdateBookingTemplate = (
  bookingTemplateForm: BookingTemplateForm
): BookingTemplateTypes.CreateUpdateBookingTemplate => {
  return {
    TemplateName: bookingTemplateForm.BookingTemplateName,
    BookingParty: bookingTemplateForm.BookingParty,
    BookingPartyType: bookingTemplateForm.BookingPartyType,
    Remark: bookingTemplateForm.Comment,
    Shipper: bookingTemplateForm.Shipper
      ? partnerFormToBookingTemplatePartner(bookingTemplateForm.Shipper)
      : null,
    Consignee: bookingTemplateForm.Consignee
      ? partnerFormToBookingTemplatePartner(bookingTemplateForm.Consignee)
      : null,
    Notifier: bookingTemplateForm.Notifier
      ? partnerFormToBookingTemplatePartner(bookingTemplateForm.Notifier)
      : null,
    // transportRequests
    FromTransportRequests: bookingTemplateForm.FromStops,
    FromTransferPlaceType: bookingTemplateForm.FromTransferPlace,
    ToTransportRequests: bookingTemplateForm.ToStops,
    ToTransferPlaceType: bookingTemplateForm.ToTransferPlace,
    BOLType: bookingTemplateForm.BillOfLadingType
      ? bookingTemplateForm.BillOfLadingType.value
      : "",
    CountryOfOrigin: bookingTemplateForm.CountryOfOrigin?.CountryCode ?? "",
    PLR: bookingTemplateForm.PLR,
    PFD: bookingTemplateForm.PFD,
    POL: bookingTemplateForm.POL,
    POD: bookingTemplateForm.POD,
    IncoTerms: bookingTemplateForm.Incoterm
      ? bookingTemplateForm.Incoterm.TermsCode
      : "",
    IncoTermsPoint: bookingTemplateForm.IncotermPoint
      ? bookingTemplateForm.IncotermPoint.PointCode
      : "",
    ShippingType: bookingTemplateForm.ShippingType,
    Details: bookingTemplateForm.Details.filter((detail) =>
      ObjectUtilities.hasSomeNonFalsyValues(detail)
    ).map((detail) => {
      return { ...detail, Temperature: detail.Temperature?.value ?? null };
    }),
    HSCodes: bookingTemplateForm.HSCodes,
    ModifiedDate: null,
    LastUsedBy: "",
    ModifiedBy: "",
    LastUsedDate: null,
  };
};
